<template>
  <div 
    ref="containerRef"
    class="confirmation-container d-flex align-items-center justify-content-center"
    :class="{ 'compact-view': asistenciaConfirmada }"
  >
    <div 
      class="confirmation-card p-4 p-md-5 rounded-4 shadow-lg"
      :class="{ 'compact-card': asistenciaConfirmada }"
      ref="formCardRef"
    >
      <!-- FINAL MESSAGE -->
      <div v-if="asistenciaConfirmada" class="qr-view text-center">
        <p class="qr-header">Thank you!</p>
        <p class="qr-instructions">
          Thank you for confirming your attendance.
        </p>
      </div>

      <!-- FORM -->
      <div v-else>
        <div class="form-header">
          <h2 ref="tituloAnimado" class="form-title">
            Confirm Attendance
          </h2>
          <br>

          <div class="progress-container">
            <div
              class="progress-bar"
              :style="{ width: ((currentStep - 1) / (totalSteps - 1)) * 100 + '%' }"
            ></div>
          </div>
          <p class="form-subtitle">Step {{ currentStep }} of {{ totalSteps }}</p>
        </div>

        <form @submit.prevent="handleSubmit" class="confirmation-form">
          <!-- Step 1 -->
          <div v-if="currentStep === 1" class="step-box">
            <div class="form-group">
              <label class="form-label">Name</label>
              <input
                v-model.trim="name"
                type="text"
                class="form-control"
                placeholder="Enter your name"
                required
              >
            </div>
            <div class="form-group">
              <label class="form-label">Phone</label>
              <input
                v-model.trim="phone"
                type="tel"
                class="form-control"
                placeholder="10-digit mobile number"
                required
              >
            </div>
          </div>

          <!-- Step 2 -->
          <div v-if="currentStep === asistenciaStep" class="step-box">
            <label class="form-label">Will you attend?</label>
            <div class="radio-group">
              <label><input type="radio" value="si" v-model="attending"> Yes</label>
              <label><input type="radio" value="no" v-model="attending"> No</label>
            </div>
          </div>

          <!-- Step 3 -->
          <div v-if="currentStep === totalSteps && attending !== 'no'" class="step-box">
            <label class="form-label">How many will attend?</label>
            <select v-model.number="guests" class="form-select" required>
              <option :value="null" disabled>Select an option</option>
              <option v-for="n in guestsOptions" :key="n" :value="n">{{ n }}</option>
            </select>
          </div>

          <!-- Buttons -->
          <div class="d-flex justify-content-between mt-3 buttons-row">
            <button
              v-if="currentStep > 1"
              type="button"
              class="submit-button secondary"
              @click="prevStep"
            >
              Back
            </button>

            <button
              v-if="currentStep < totalSteps"
              type="button"
              class="submit-button"
              @click="nextStep"
            >
              Next
            </button>

            <button
              v-if="currentStep === totalSteps"
              type="submit"
              class="submit-button"
            >
              Confirm
            </button>
          </div>

          <div v-if="errorMessage" class="alert alert-danger mt-3">
            {{ errorMessage }}
          </div>
        </form>
      </div>
    </div>
  </div>
</template>

<script>
import { ref, onMounted, computed, nextTick } from 'vue'
import axios from 'axios'
import baseUrl from '@/api'
import gsap from 'gsap'

export default {
  name: 'AttendanceConfirmation',
  props: {
    idInvitado: Number,
    nombre: String,
    idFestejado: Number
  },
  setup(props) {
    const name = ref('')
    const phone = ref('')
    const attending = ref('')
    const guests = ref(null)
    const guestsOptions = ref([1,2,3,4,5,6])
    const asistenciaConfirmada = ref(false)
    const currentStep = ref(1)
    const errorMessage = ref('')

    const formCardRef = ref(null)
    const tituloAnimado = ref(null)
    const containerRef = ref(null)

    const isValidInvitado = computed(() => Number(props.idInvitado) > 0)

    const totalSteps = computed(() => 3)
    const asistenciaStep = computed(() => 2)

    const nextStep = () => {
      errorMessage.value = ''

      if (currentStep.value === 1) {
        if (!name.value || !phone.value) {
          errorMessage.value = 'Please enter your name and phone number'
          return
        }
      }

      if (currentStep.value === asistenciaStep.value) {
        if (!attending.value) {
          errorMessage.value = 'Please select whether you will attend or not'
          return
        }
        if (attending.value === 'no') {
          currentStep.value = totalSteps.value
          return
        }
      }

      if (currentStep.value === totalSteps.value && attending.value === 'si') {
        if (!guests.value) {
          errorMessage.value = 'Please select how many will attend'
          return
        }
      }

      currentStep.value++
    }

    const prevStep = () => {
      errorMessage.value = ''
      currentStep.value--
    }

    const handleSubmit = async () => {
      try {
        const payload = {
          nombre_asistencia: name.value,
          telefono_invitado: phone.value,
          asistira: attending.value,
          num_pases: attending.value === 'no' ? 0 : guests.value,
          id_festejada: props.idFestejado,
          invitedby: isValidInvitado.value ? (props.nombre || 'xv') : 'xv'
        }

        if (isValidInvitado.value) {
          payload.id_invitado = props.idInvitado
        }

        await axios.post(`${baseUrl}/asistencia`, payload)
        asistenciaConfirmada.value = true
      } catch (e) {
        errorMessage.value = 'Error confirming attendance'
      }
    }

    onMounted(async () => {
      if (!isValidInvitado.value) {
        guestsOptions.value = [1,2,3,4,5,6]
      }

      await nextTick()

      gsap.fromTo(
        formCardRef.value,
        { opacity: 0, y: 40, scale: 0.96 },
        { opacity: 1, y: 0, scale: 1, duration: 0.6, ease: 'power2.out' }
      )

      gsap.fromTo(
        tituloAnimado.value,
        { y: -20, opacity: 0 },
        { y: 0, opacity: 1, duration: 0.5 }
      )
    })

    return {
      name,
      phone,
      attending,
      guests,
      guestsOptions,
      asistenciaConfirmada,
      currentStep,
      totalSteps,
      asistenciaStep,
      nextStep,
      prevStep,
      handleSubmit,
      errorMessage,
      formCardRef,
      tituloAnimado,
      containerRef
    }
  }
}
</script>

<style scoped>
/* Estilos base */
.confirmation-container {
  min-height: 70vh;
  padding: 2rem 1rem;

  transition: all 0.3s ease;
}

.confirmation-card {
  width: 100%;
  max-width: 500px;
  background: white;
  background: linear-gradient(to bottom, #ffffff, #f9f9f9);
  border: 1px solid #e0e0e0;
  transition: all 0.3s ease;
}

/* Vista compacta para QR */
.compact-view {
  min-height: 70vh;
  align-items: flex-start;
  padding-top: 4rem;
}

.compact-card {
  max-width: 400px;
  padding: 2rem;
}

/* Encabezado del formulario */
.form-header {

  margin-bottom: 2rem;
  text-align: center;
}

.form-title {
  font-family: 'CynthiaJuneJF', cursive;
  font-size: 46px !important;
 background: linear-gradient(110deg, #e6a6c6 30%, #ff85ae 45%, #faadd8 50%, #ff85c2 55%, #ef90ae 70%);
  background-size: 200% auto;
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0px 2px 3px rgba(0,0,0,0.3));
  display: block; 
}

.form-subtitle {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
}

/* Grupos de formulario */
.form-group {
  margin-bottom: 1.5rem;
}

.form-label {
  display: block;
  margin-bottom: 0.5rem;
  font-family: 'Karla', sans-serif;
  font-weight: 500;
  color: #495057;
}

/* Controles de formulario */
.form-control,
.form-select {
  width: 100%;
  padding: 0.75rem 1rem;
  font-size: 1rem;
  border: 1px solid #ced4da;
  border-radius: 0.5rem;
  background-color: rgba(255, 255, 255, 0.8);
  transition: border-color 0.3s, box-shadow 0.3s;
}

.form-control:focus,
.form-select:focus {
  border-color: #bf953f;
  box-shadow: 0 0 0 0.25rem rgba(213, 146, 176, 0.25);
  outline: none;
}

/* Radio buttons */
.radio-group {
  display: flex;
  gap: 1.5rem;
}

.radio-option {
  display: flex;
  align-items: center;
}

.radio-input {
  margin-right: 0.5rem;
  accent-color: #bf953f;
}

.radio-label {
  font-family: 'Karla', sans-serif;
  color: #495057;
}

/* Botón de enviar */
.submit-button {
  width: 100%;
  padding: 0.75rem;
  font-family: 'FeelingPassionate', cursive;
  font-size: 1.35rem;
 /* Imagen Dorada */
  background-image: url('@/assets/dorado.jpeg');
  background-size: cover;
  background-position: center;
  background-color: #bf953f; /* Respaldo */

  /* Texto */
  color: #f7f6f6 !important;
  -webkit-text-fill-color: #ffffff !important;
  text-decoration: none;  border: none;
  border-radius: 0.5rem;
  cursor: pointer;
  transition: background-color 0.3s;
}

.submit-button:hover {
  background-image: url('@/assets/dorado.jpeg');
}

.submit-button:disabled {
  background-image: url('@/assets/dorado.jpeg');
  opacity: 0.7;
  cursor: not-allowed;
}

/* Vista QR */
.qr-view {
  padding: 1rem;
}

.qr-header {
  font-family: 'Westony', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin-bottom: 1.5rem;
}

.confirmation-thanks {
  font-family: 'Karla', sans-serif;
  font-weight: 600;
  color: #354257;
  font-size: 1.25rem;
  margin-bottom: 1rem;
}

.qr-wrapper {
  margin: 1rem 0;
}

.qr-code {
  width: 180px;
  height: 180px;
  border: 8px solid white;
  border-radius: 0.5rem;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
}

.decoration-crown {
  width: auto;
  height: 80px;
  margin: 0.5rem 0;
}

.event-title {
  font-family: 'Parisienne', cursive;
  font-size: 1.75rem;
  color: #5a3d5c;
  margin: 0.5rem 0;
}

.qr-instructions {
  font-family: 'Karla', sans-serif;
  color: #6c757d;
  font-size: 1rem;
  margin-top: 1rem;
}

/* Mensajes de error */
.alert {
  padding: 0.75rem 1rem;
  border-radius: 0.5rem;
}

.qr-error-message {
  color: #dc3545;
  font-weight: 500;
}

.loading-indicator {
  font-size: 0.875rem;
  color: #6c757d;
  margin-top: 0.5rem;
}

/* Responsive */
@media (max-width: 576px) {
  .confirmation-card {
    padding: 1.5rem;
  }
  
  .form-title {
    font-size: 1.75rem;
  }
  
  .radio-group {
    gap: 1rem;
  }
}




.progress-container {
  height: 8px;
  margin-top:40px;
  background-color: #e0e0e0;
  border-radius: 4px;
  margin-bottom: 1rem;
  overflow: hidden;
}

.progress-bar {
  height: 100%;
  background-color: #bf953f; /* color rosado bonito */
  transition: width 0.4s ease;
}

</style>